// Composition root. Wire the app here, keep logic out.

import './styles/tokens.css';
import './styles/base.css';
import './styles/app.css';
import './styles/sheets.css';
import './styles/cue.css';
import './styles/board.css';
import './styles/stage.css';
import './styles/board-cover.css';
import './styles/wheel-box.css';
import './styles/panels.css';
import './styles/patterns.css';
import './styles/cards.css';
import './styles/tray.css';
import './styles/caster.css';
import './styles/caster-select.css';
import './styles/lasting.css';
import './styles/motes.css';
import './styles/room.css';
import './styles/cast-sheet.css';
import './styles/compact.css';
import './styles/dither-ramp.css';
import './styles/mono.css';

import { createBoard } from './components/Board';
import { createBoardCover } from './components/BoardCover';
import { createCasterRead } from './components/CasterRead';
import { createCasterRow } from './components/CasterRow';
import { createCasterSelect } from './components/CasterSelect';
import { createCastSheet } from './components/CastSheet';
import { createCoinFlip } from './components/CoinFlip';
import { createCueBar } from './components/CueBar';
import { createDitherDefs } from './components/DitherDefs';
import { createEnergyMotes } from './components/EnergyMotes';
import { createEnergyRow } from './components/EnergyRow';
import { createGraveyard } from './components/Graveyard';
import { createHand } from './components/Hand';
import { createHistoryNav } from './components/HistoryNav';
import { createInspector } from './components/Inspector';
import { createLastingBands } from './components/LastingBands';
import { createLog } from './components/Log';
import { createPageGrain } from './components/PageGrain';
import { createRoomNotice } from './components/RoomNotice';
import { createStage } from './components/Stage';
import { createTooltip } from './components/Tooltip';
import { createTopBar } from './components/TopBar';
import { createWheel } from './components/Wheel';
import {
  APP_ROOT_SELECTOR,
  ROLL_BUTTON_SELECTOR,
  SPECTATE_QUERY_PARAM,
  STAGE_DIE_SELECTOR,
} from './constants';
import { otherSide } from './engine/pieces';
import type { Side } from './engine/types';
import type { MatchMode } from './game/state';
import { createCompactLayout } from './layout/compactLayout';
import type { LayoutParts, MountLayout } from './layout/parts';
import { createWideLayout } from './layout/wideLayout';
import { el, render, requireElement } from './lib/dom';
import { startTheme } from './lib/theme';
import { readRoomCode } from './room/protocol';
import { startAiDriver } from './state/aiDriver';
import { startBoardCover } from './state/boardCover';
import { startCastKeys } from './state/castChoice';
import { startEnergySpend } from './state/energySpend';
import { gameStore } from './state/game';
import { isReviewing } from './state/history';
import { startHistoryKeys } from './state/historyKeys';
import { startLastingShown } from './state/lastingShown';
import { type LayoutMode, layoutStore, startLayout } from './state/layout';
import { localSide, localSideStore } from './state/localSide';
import { logOpenStore } from './state/logOpen';
import { startNarrator } from './state/narrator';
import { startFirstMatch } from './state/newMatch';
import { restoreLinkedMatch, restoreSavedMatch, startMatchSave } from './state/persist';
import { startRollKey } from './state/rollKey';
import { joinRoom } from './state/room';
import { isInRoom } from './state/roomStore';
import { startSoundDriver } from './state/soundDriver';
import { stageStore, startStage } from './state/stage';
import { startSuggestion } from './state/suggestion';
import { startTabTitle } from './state/tabTitle';
import { startTray } from './state/tray';
import { startSelectionKeys } from './state/ui';
import { isRollMoment, subscribeView } from './state/view';

startTheme();
startLayout();
startStage();
startNarrator();
startLastingShown();
startHistoryKeys();
startSelectionKeys();
startCastKeys();
startRollKey();
startSoundDriver();

// A path with a room code opens that room. A link with a match loads that match. A reload
// continues the saved match. With none of them, a new one starts, and in Godsmode it waits
// until the player picks the casters.
const roomCode = readRoomCode(window.location.pathname);
if (roomCode) joinRoom(roomCode);
else if (!restoreLinkedMatch() && !restoreSavedMatch()) startFirstMatch();
// After the start, so a page that opens on the caster select has the board covered at once.
startBoardCover();
startMatchSave();
startTabTitle();
// After the load, so a loaded match shows no energy as just paid.
startEnergySpend();
startTray();

const cueBar = createCueBar();
// Godsmode: a sheet for each side. The sheet of the side of this screen is the desk, and
// the other is the score, which is only read. In a room the side can change at a new
// match, so each side has both, and the layout mounts the pair that fits.
const teamSheets: Record<Side, Record<'desk' | 'score', HTMLElement>> = {
  gold: { desk: createCasterRow('gold', 'desk'), score: createCasterRow('gold', 'score') },
  violet: { desk: createCasterRow('violet', 'desk'), score: createCasterRow('violet', 'score') },
};
const board = createBoard();
const parts: LayoutParts = {
  topBar: createTopBar(),
  hand: createHand(),
  cueBar,
  boardFrame: el('div', { className: 'board-frame' }, [
    board,
    createLastingBands(board),
    createStage(requireElement(ROLL_BUTTON_SELECTOR, cueBar)),
    createBoardCover(board),
    createCoinFlip(requireElement(ROLL_BUTTON_SELECTOR, cueBar)),
  ]),
  historyNav: createHistoryNav(),
  graveyard: createGraveyard(),
  energyRow: createEnergyRow(),
  wheelBox: el('section', { className: 'wheel-box', ariaLabel: 'The wheel' }, [createWheel()]),
  moonTeam: teamSheets.violet.score,
  sunTeam: teamSheets.gold.desk,
  inspector: createInspector(),
  casterRead: createCasterRead(),
  logBox: el('section', { className: 'log-box', ariaLabel: 'Log' }, [createLog()]),
};

// A change of layout moves the same parts into a different tree.
const mountLayout: Record<LayoutMode, MountLayout> = {
  wide: createWideLayout(parts),
  compact: createCompactLayout(parts),
};
const app = el('main', { className: 'app' });
// The class says which layout is up, whether the match has casters, whether its log is open, whether the board shows an earlier step, and whether a roll is on.
const drawAppClass = (): void => {
  const gods = gameStore.get().mode === 'gods';
  app.className = `app app--${layoutStore.get()}${gods ? ' app--gods' : ''}${gods && logOpenStore.get() ? ' app--log-open' : ''}${isReviewing() ? ' app--reviewing' : ''}${isRollMoment() ? ' app--rolling' : ''}`;
};
// A match with casters has its own tree in each layout.
let mountedMode: MatchMode | null = null;
const drawLayout = (): void => {
  drawAppClass();
  mountedMode = gameStore.get().mode;
  // The names of the two parts are from when the player was always Sun: the top sheet and the bottom one.
  parts.moonTeam = teamSheets[otherSide(localSide())].score;
  parts.sunTeam = teamSheets[localSide()].desk;
  mountLayout[layoutStore.get()](app, mountedMode);
};
drawLayout();
layoutStore.subscribe(drawLayout);
localSideStore.subscribe(drawLayout);
subscribeView(() => {
  if (gameStore.get().mode === mountedMode) drawAppClass();
  else drawLayout();
});
stageStore.subscribe(drawAppClass);
logOpenStore.subscribe(drawAppClass);

render(requireElement(APP_ROOT_SELECTOR), app);
// The mono look fills shapes with these patterns by id.
document.body.append(createDitherDefs());
// The grain of the page, behind every block.
document.body.append(createPageGrain());
// The coins of energy that fly from the die to the pips, over every block.
document.body.append(createEnergyMotes(requireElement(STAGE_DIE_SELECTOR, parts.boardFrame)));
// The one tooltip, for every control with `data-tip`.
document.body.append(createTooltip());
// The card of a cast to confirm, on a backdrop over the whole window.
document.body.append(createCastSheet());
// The caster select of Godsmode, which is also the lobby of a room, and the notice when a room ends.
document.body.append(createCasterSelect(), createRoomNotice());

// `?spectate` lets the AI play both sides. Useful for watching the boardwide. A room has no AI.
const spectate = new URLSearchParams(window.location.search).has(SPECTATE_QUERY_PARAM);
startAiDriver(spectate ? ['gold', 'violet'] : isInRoom() ? [] : ['violet']);
// With the AI on both sides, nobody reads a suggestion or a preview.
if (!spectate) startSuggestion();
